import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import styles from './OreMultiSelect.module.css'
import { OreMultiSelectTag } from './OreMultiSelectTag'
import { OreMultiSelectTagCounter } from './OreMultiSelectTagCounter'
import { OreCardContainer, OreCardWrapper } from '@edenredespana/oreneta'
import { Trans, useTranslation } from 'react-i18next'
import { useUserData } from '@/User/ui/context'
import { transactionsTranslation } from '@/Transactions/ui/transactionsTranslation'
import { UserType } from '@/User/domain/UserType'

// Fallback inline SVG para el chevron (versión similar a OreSelect, 24x24, usa currentColor)
const ChevronDown: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg
    width={24}
    height={24}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
    focusable="false"
    {...props}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M3.97 8.344a.75.75 0 0 1 1.06 0L12 15.313l6.97-6.97a.75.75 0 0 1 1.06 1.061l-7.146 7.146a1.249 1.249 0 0 1-1.768 0L3.97 9.404a.75.75 0 0 1 0-1.06Z"
      fill="currentColor"
    />
  </svg>
)

// Tipado de opción
export interface OreMultiSelectOption {
  value: string
  label: string
  // Tokens adicionales de búsqueda (ej: CIF). No se muestran en UI directamente.
  searchTokens?: string[]
}

export interface OreMultiSelectProps {
  value: string[]
  options: OreMultiSelectOption[]
  onChange: (values: string[]) => void
  label?: string
  placeholder?: string // placeholder en el control cerrado
  searchPlaceholder?: string // placeholder del cuadro de búsqueda
  disabled?: boolean
  hasError?: boolean
  errorMessage?: string
  maxVisibleTags?: number // cuántos tags mostrar antes de colapsar con +N
  selectAllLabel?: string
  searchAriaLabel?: string
  className?: string
}

/**
 * Componente MultiSelect con búsqueda y tags.
 * No depende internamente de react-hook-form, pero se puede integrar con Controller.
 *
 * Accesibilidad / UX:
 * - Navegación teclado: Enter abre/cierra, Esc cierra, ArrowDown/up dentro de lista.
 * - Click fuera cierra.
 */
export const OreMultiSelect: React.FC<OreMultiSelectProps> = ({
  value,
  options,
  onChange,
  label,
  placeholder,
  searchPlaceholder,
  disabled,
  hasError,
  errorMessage,
  searchAriaLabel ,
  className,
}) => {
  const tagGapPx = 4
  const triggerReservedPx = 56
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [autoFocusSearch, setAutoFocusSearch] = useState(false)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const listRef = useRef<HTMLUListElement | null>(null)
  const triggerRef = useRef<HTMLDivElement | null>(null)
  const [focusIndex, setFocusIndex] = useState<number>(-1)
  const dropdownRef = useRef<HTMLDivElement | null>(null)
  const [dropdownPos, setDropdownPos] = useState<{top:number;left:number;width:number}>({top:0,left:0,width:0})
  const [dropdownMaxHeight, setDropdownMaxHeight] = useState(420)
  const searchInputRef = useRef<HTMLInputElement | null>(null)
  const [triggerWidth, setTriggerWidth] = useState(0)
  const [visibleFullTagCount, setVisibleFullTagCount] = useState(0)
  const [useTruncatedTagFallback, setUseTruncatedTagFallback] = useState(false)
  const [truncatedTagMaxWidth, setTruncatedTagMaxWidth] = useState(24)
  const tagMeasureRefs = useRef<Record<string, HTMLSpanElement | null>>({})
  const counterMeasureRefs = useRef<Record<number, HTMLSpanElement | null>>({})
  const { t } = useTranslation()
  const { merchant } = useUserData()
  const { userType } = useUserData()
  const isNursery = Boolean(merchant?.isNursery)
  const selectedOptions = useMemo(
    () => value.map(v => options.find(o => o.value === v)).filter((opt): opt is OreMultiSelectOption => Boolean(opt)),
    [value, options]
  )
  // Filtrado de opciones
  // Normalización para búsqueda sin tildes / diacríticos.
  const normalize = (str: string) => str
    .normalize('NFD') // separa letras y diacríticos
    .replace(/[\u0300-\u036f]/g, '') // elimina marcas de combinación
    .toLowerCase()
    .trim()

  const filtered = useMemo(() => {
    const term = normalize(search)
    if (!term) return options
    return options.filter(o => {
      const labelMatch = normalize(o.label).includes(term)
      if (labelMatch) return true
      if (o.searchTokens && o.searchTokens.length) {
        return o.searchTokens.some(tok => normalize(tok).includes(term))
      }
      return false
    })
  }, [search, options])
  const tagMaxWidthByRule = Math.max(Math.floor(triggerWidth * 0.6), 24)

  // Manejo de cierre por click fuera
  // Outside click incluyendo portal
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node
      if (
        triggerRef.current && triggerRef.current.contains(target)
      ) return
      if (dropdownRef.current && dropdownRef.current.contains(target)) return
      setOpen(false)
      setFocusIndex(-1)
    }
    if (open) document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  // Recalcular posición cuando se abre, y en resize / scroll
  useEffect(() => {
    const calc = () => {
      if (!triggerRef.current || !open) return
      const rect = triggerRef.current.getBoundingClientRect()
      const nextTop = window.scrollY + rect.top + rect.height + (-5)
      const topInViewport = rect.top + rect.height + (-5)
      const availableHeight = window.innerHeight - topInViewport - 32
      setDropdownPos({
        top: nextTop, // ajustado para quedar más pegado
        left: window.scrollX + rect.left,
        width: rect.width,
      })
      setDropdownMaxHeight(Math.max(Math.min(420, Math.floor(availableHeight)), 120))
    }
    calc()
    if (open) {
      window.addEventListener('resize', calc)
      window.addEventListener('scroll', calc, true)
    }
    return () => {
      window.removeEventListener('resize', calc)
      window.removeEventListener('scroll', calc, true)
    }
  }, [open])

  // Toggle selección de una opción
  const toggleValue = (val: string) => {
    if (value.includes(val)) {
      onChange(value.filter(v => v !== val))
    } else {
      onChange([...value, val])
    }
  }

  const allSelected = value.length === options.length && options.length > 0

  const handleSelectAll = () => {
    if (allSelected) {
      onChange([])
    } else {
      onChange(options.map(o => o.value))
    }
  }

  useEffect(() => {
    const node = triggerRef.current
    if (!node) return

    const updateWidth = () => {
      const nextWidth = Math.round(node.getBoundingClientRect().width)
      setTriggerWidth(prevWidth => (prevWidth === nextWidth ? prevWidth : nextWidth))
    }

    updateWidth()

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(updateWidth)
      observer.observe(node)
      return () => observer.disconnect()
    }

    window.addEventListener('resize', updateWidth)
    return () => window.removeEventListener('resize', updateWidth)
  }, [])

  useLayoutEffect(() => {
    let nextVisibleFullTagCount = 0
    let nextUseTruncatedTagFallback = false
    let nextTruncatedTagMaxWidth = 24

    if (selectedOptions.length === 0) {
    } else {
      const maxSelectableVisible = selectedOptions.length

      const availableWidth = Math.max(triggerWidth - triggerReservedPx, 0)
      const getTagWidth = (tagValue: string) => tagMeasureRefs.current[tagValue]?.offsetWidth ?? 0
      const getCounterWidth = (count: number) => counterMeasureRefs.current[count]?.offsetWidth ?? 0

      for (let fullCount = maxSelectableVisible; fullCount >= 1; fullCount--) {
        const hiddenCount = selectedOptions.length - fullCount
        const counterWidth = hiddenCount > 0 ? getCounterWidth(hiddenCount) : 0
        const itemsCount = fullCount + (hiddenCount > 0 ? 1 : 0)
        const gapsWidth = itemsCount > 1 ? (itemsCount - 1) * tagGapPx : 0
        const fullWidth = selectedOptions
          .slice(0, fullCount)
          .reduce((acc, opt) => acc + Math.min(getTagWidth(opt.value), tagMaxWidthByRule), 0)

        if (fullWidth + counterWidth + gapsWidth <= availableWidth) {
          nextVisibleFullTagCount = fullCount
          nextUseTruncatedTagFallback = false
          nextTruncatedTagMaxWidth = 24
          break
        }
      }

      if (nextVisibleFullTagCount === 0) {
        const fallbackHiddenCount = Math.max(selectedOptions.length - 1, 0)
        const fallbackCounterWidth = fallbackHiddenCount > 0 ? getCounterWidth(fallbackHiddenCount) : 0
        const fallbackGap = fallbackHiddenCount > 0 ? tagGapPx : 0

        nextUseTruncatedTagFallback = true
        nextTruncatedTagMaxWidth = Math.max(
          Math.min(tagMaxWidthByRule, availableWidth - fallbackCounterWidth - fallbackGap),
          24
        )
      }
    }

    setVisibleFullTagCount(prev => (prev === nextVisibleFullTagCount ? prev : nextVisibleFullTagCount))
    setUseTruncatedTagFallback(prev => (prev === nextUseTruncatedTagFallback ? prev : nextUseTruncatedTagFallback))
    setTruncatedTagMaxWidth(prev => (prev === nextTruncatedTagMaxWidth ? prev : nextTruncatedTagMaxWidth))
  }, [selectedOptions, triggerWidth])

  // Tags visibles + contador restante
  const showTruncatedTag = useTruncatedTagFallback && selectedOptions.length > 0
  const visibleTags = showTruncatedTag
    ? selectedOptions.slice(0, 1)
    : selectedOptions.slice(0, visibleFullTagCount)
  const hiddenCount = showTruncatedTag
    ? Math.max(selectedOptions.length - 1, 0)
    : selectedOptions.length - visibleTags.length
  const tagMaxWidthForVisible = showTruncatedTag
    ? Math.min(truncatedTagMaxWidth, tagMaxWidthByRule)
    : tagMaxWidthByRule

  // Para navegación de teclado dentro de la lista
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return
    if (e.key === 'Enter') {
      e.preventDefault()
      setOpen(o => !o)
    } else if (e.key === 'Escape') {
      setOpen(false)
      setFocusIndex(-1)
    } else if (e.key === 'ArrowDown') {
      if (!open) {
        setOpen(true)
        setFocusIndex(0)
      } else {
        setFocusIndex(i => Math.min(i + 1, filtered.length - 1))
      }
    } else if (e.key === 'ArrowUp') {
      if (open) {
        setFocusIndex(i => Math.max(i - 1, 0))
      }
    } else if (e.key === ' ') {
      if (open && focusIndex >= 0) {
        const opt = filtered[focusIndex]
        if (opt) toggleValue(opt.value)
      }
    }
  }

  useEffect(() => {
    if (open && focusIndex >= 0 && listRef.current) {
      const el = listRef.current.children[focusIndex] as HTMLElement | undefined
      el?.scrollIntoView({ block: 'nearest' })
    }
  }, [open, focusIndex])

  // Enfocar automáticamente el input de búsqueda al abrir
  useEffect(() => {
    if (open) {
      setAutoFocusSearch(true)
      // defer para asegurar montaje; prevenir scroll al enfocar
      requestAnimationFrame(() => {
        try {
          // browsers modernos soportan preventScroll
          (searchInputRef.current as any)?.focus({ preventScroll: true })
        } catch {
          searchInputRef.current?.focus()
        }
      })
    } else {
      setAutoFocusSearch(false)
    }
  }, [open])

  const mailtoText= isNursery? 'transactions.table.filters.local.noOptionsDescriptionNursery' : 'transactions.table.filters.local.noOptionsDescription'

  return (
    <div
      ref={containerRef}
      className="ore-input"
      style={{ width: '100%', minWidth: 0, maxWidth: '100%', flex: '1 1 0%' }}
    >
      {label && <label className={styles['ore-multiselect__label']}>{label}</label>}
      <div
        ref={triggerRef}
  className={`ore-input__input-wrapper ore-input__input-wrapper--select ${disabled ? 'ore-input__input-wrapper--disabled' : ''} ${hasError ? 'ore-input__input-wrapper--error' : ''} ${className || ''}`}
        tabIndex={disabled ? -1 : 0}
        onKeyDown={handleKeyDown}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{
          position: 'relative',
          display: 'flex',
          width: '100%',
          minWidth: 0,
          maxWidth: '100%',
          flexWrap: 'nowrap',
          gap: '4px',
          alignItems: 'center',
          paddingRight: '45px',
          paddingLeft: 'var(--spacing-small)',
          overflow: 'hidden'
        }}
        onClick={() => {
          if (disabled) return
          setOpen(o => !o)
        }}
      >
        <span className={`ore-input__input-wrapper--select--icons ${open ? styles['ore-multiselect__chevron-rotated'] : styles['ore-multiselect__chevron-static']}`} aria-hidden>
          <ChevronDown className="icon-chevron-down" />
        </span>
        {selectedOptions.length === 0 && (
          <span className={styles['ore-multiselect__placeholder']}>{placeholder}</span>
        )}
        {visibleTags.map((opt, idx) => {
          return (
            <OreMultiSelectTag
              key={opt.value}
              value={opt.value}
              label={opt.label}
              onRemove={toggleValue}
              truncate
              style={{
                maxWidth: `${showTruncatedTag && idx === 0 ? Math.min(truncatedTagMaxWidth, tagMaxWidthForVisible) : tagMaxWidthForVisible}px`,
              }}
            />
          )
        })}
        <OreMultiSelectTagCounter count={hiddenCount} />
      </div>
      {selectedOptions.length > 0 && (
        <div className={styles['ore-multiselect__measure']} aria-hidden>
          {selectedOptions.map(opt => (
            <OreMultiSelectTag
              key={`measure-tag-${opt.value}`}
              value={opt.value}
              label={opt.label}
              onRemove={() => {}}
              truncate
              style={{ maxWidth: `${tagMaxWidthByRule}px` }}
              spanRef={el => {
                tagMeasureRefs.current[opt.value] = el
              }}
            />
          ))}
          {Array.from({ length: selectedOptions.length }, (_, index) => {
            const count = index + 1
            return (
              <OreMultiSelectTagCounter
                key={`measure-counter-${count}`}
                count={count}
                spanRef={el => {
                  counterMeasureRefs.current[count] = el
                }}
              />
            )
          })}
        </div>
      )}
  {hasError && errorMessage && <span className={styles['ore-input__error-message']}>{errorMessage}</span>}
      {open && !disabled && createPortal(
        <div ref={dropdownRef} className={styles['ore-multiselect__dropdown']} style={{ position: 'absolute', zIndex: 1000, width: dropdownPos.width, left: dropdownPos.left, top: dropdownPos.top, maxHeight: `${dropdownMaxHeight}px` }}>
          <OreCardContainer tone="white" hasShadow radius="bottom">
            <OreCardWrapper space="small">
              <div className={styles['ore-multiselect__search-wrapper']}>
                <input
                  aria-label={searchAriaLabel}
                  placeholder={searchPlaceholder}
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className={styles['ore-multiselect__search-input']}
                  ref={searchInputRef}
                  // autoFocus adicional por si el navegador lo respeta tras portal mount
                  autoFocus={autoFocusSearch}
                />
                <span className={styles['ore-multiselect__search-icon']}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14Zm0 16c-4.97 0-9-4.03-9-9s4.03-9 9-9 9 4.03 9 9c0 2.12-.74 4.07-1.98 5.61l4.33 4.34a1 1 0 0 1-1.42 1.42l-4.34-4.33A8.962 8.962 0 0 1 11 20Z" fill="currentColor" />
                  </svg>
                </span>
              </div>
              {filtered.length > 0 && (
                <label className={styles['ore-multiselect__select-all']}>
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={handleSelectAll}
                  />
                  {t(
                      userType === UserType.NURSERY
                    ? transactionsTranslation.table.filters.local.selectAllNursery
                    : transactionsTranslation.table.filters.local.selectAll)}
                </label>
              )}
              <div
                className={
                  filtered.length === 0
                    ? styles['ore-multiselect__options-container-noresults']
                    : styles['ore-multiselect__options-container']
                }
              >
                <ul
                  ref={listRef}
                  role="listbox"
                  aria-multiselectable
                  className={styles['ore-multiselect__options']}
                >
                  {filtered.length === 0 && (
                    <li className={`${styles['ore-multiselect__option']} ${styles['ore-multiselect__option--empty']}`}>
                      <div className={styles['ore-multiselect__empty-primary']}>{t('transactions.table.filters.local.noOptionsTitle')}</div>
                      <div className={styles['ore-multiselect__empty-secondary']}>
                        <Trans t={t} components={
                          [
                            <a href={`mailto:${import.meta.env.VITE_CONTACT_EMAIL}`} />
                          ]
                        }>              
                        {t(mailtoText,{
                          CONTACT_EMAIL: import.meta.env.VITE_CONTACT_EMAIL,
                        })}
                        </Trans>
                      </div>
                    </li>
                  )}
                  {filtered.map((opt, idx) => {
                    const selected = value.includes(opt.value)
                    const focused = idx === focusIndex
                    return (
                      <li
                        key={opt.value}
                        onClick={() => toggleValue(opt.value)}
                        onMouseEnter={() => setFocusIndex(idx)}
                        role="option"
                        aria-selected={selected}
                        className={`${styles['ore-multiselect__option']} ${selected ? styles['ore-multiselect__option--selected'] : ''} ${focused ? styles['ore-multiselect__option--focused'] : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleValue(opt.value)}
                          onClick={e => e.stopPropagation()}
                        />
                        <span>{opt.label}</span>
                      </li>
                    )
                  })}
                </ul>
              </div>
            </OreCardWrapper>
          </OreCardContainer>
        </div>, document.body
      )}
    </div>
  )
}